import { useState } from "react";
import type { Workshop } from "@/content/workshops";
import { useAuth } from "@/hooks/use-auth";
import {
  crewByWorkshop,
  crewFromRoster,
  type CrewAccentColor,
  type CrewMember,
} from "@/features/workshop/crew-content";
import {
  clearEffectiveOverrideField,
  setEffectiveOverride,
  useEffectiveWorkshopOverride,
} from "@/stores/useEffectiveWorkshopOverride";
import {
  AddButton,
  Field,
  ListCard,
  ReactorToggle,
  SectionFrame,
  Select,
  TextArea,
  TextInput,
} from "../editor-kit";
import { genId, moveItem, sameJson } from "../editor-utils";

const COLORS: CrewAccentColor[] = ["blue", "cyan", "green", "orange", "melon", "red"];

/** The accent each crew colour maps to, for the picker swatch. */
const SWATCH: Record<CrewAccentColor, string> = {
  blue: "var(--accent-true-blue)",
  cyan: "var(--accent-sky)",
  green: "var(--accent-green)",
  orange: "var(--accent-gold)",
  melon: "var(--accent-melon)",
  red: "var(--accent-red)",
};

type Draft = { selectedIds: string[]; custom: CrewMember[] };

function ColorSelect({
  value,
  onChange,
}: {
  value: CrewAccentColor | undefined;
  onChange: (c: CrewAccentColor) => void;
}) {
  const v = value ?? "blue";
  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden
        className="h-3.5 w-3.5 flex-shrink-0 rounded-full"
        style={{ background: SWATCH[v], boxShadow: `0 0 6px ${SWATCH[v]}` }}
      />
      <div className="min-w-0 flex-1">
        <Select
          value={v}
          onChange={(c) => onChange(c as CrewAccentColor)}
          options={COLORS.map((c) => ({ value: c, label: c, swatch: SWATCH[c] }))}
        />
      </div>
    </div>
  );
}

/**
 * Meet-the-Crew editor. "Both" model: pick which roster members appear AND
 * author bespoke cards. Baseline reproduces today's view — for a workshop
 * with hand-authored crew (WS4) the authored cards seed `custom`; otherwise
 * the whole roster is pre-selected.
 */
export function CrewEditor({ wsId }: { wsId: Workshop["id"] }) {
  const { users } = useAuth();
  const override = useEffectiveWorkshopOverride(wsId);
  const rosterCrew = crewFromRoster(users);
  const authored = crewByWorkshop[wsId] ?? [];

  const defaultBaseline: Draft =
    authored.length > 0
      ? { selectedIds: [], custom: authored }
      : { selectedIds: rosterCrew.map((m) => m.id), custom: [] };
  const current: Draft = override?.crew
    ? {
        selectedIds: override.crew.rosterIds ?? rosterCrew.map((m) => m.id),
        custom: override.crew.custom ?? [],
      }
    : defaultBaseline;

  const [draft, setDraft] = useState<Draft>(current);

  const toggle = (id: string) =>
    setDraft((d) => ({
      ...d,
      selectedIds: d.selectedIds.includes(id)
        ? d.selectedIds.filter((x) => x !== id)
        : [...d.selectedIds, id],
    }));
  const setCustom = (custom: CrewMember[]) => setDraft((d) => ({ ...d, custom }));
  const setCard = (i: number, patch: Partial<CrewMember>) =>
    setCustom(draft.custom.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const setAgent = (i: number, patch: Partial<NonNullable<CrewMember["agent"]>>) =>
    setCustom(
      draft.custom.map((c, j) =>
        j === i
          ? {
              ...c,
              agent: {
                name: "",
                type: "",
                description: "",
                color: c.color ?? "blue",
                ...c.agent,
                ...patch,
              },
            }
          : c,
      ),
    );

  return (
    <SectionFrame
      description="Who appears in Meet the Crew for this workshop."
      overridden={override?.crew !== undefined}
      dirty={!sameJson(draft, current)}
      onSave={() =>
        setEffectiveOverride(wsId, {
          crew: { rosterIds: draft.selectedIds, custom: draft.custom },
        })
      }
      onReset={() => {
        clearEffectiveOverrideField(wsId, "crew");
        setDraft(defaultBaseline);
      }}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-fs-label font-semibold tracking-[0.18em] text-[color:var(--text-muted)] uppercase">
            Roster members
          </span>
          <span
            className="rounded-full px-2 py-0.5 font-mono text-fs-micro font-semibold tracking-[0.16em] uppercase"
            style={{
              color: draft.selectedIds.length > 0 ? "var(--accent-sky)" : "var(--text-muted)",
              border: `1px solid ${draft.selectedIds.length > 0 ? "rgba(var(--accent-true-blue-rgb),0.4)" : "hsl(var(--border))"}`,
              background: draft.selectedIds.length > 0 ? "rgba(var(--accent-sky-rgb),0.08)" : "transparent",
            }}
          >
            {draft.selectedIds.length} of {rosterCrew.length} selected
          </span>
        </div>
        {rosterCrew.length === 0 ? (
          <p className="text-fs-label text-[color:var(--text-muted)]">No roster members available.</p>
        ) : (
          <div
            className="overflow-hidden rounded-lg"
            style={{ border: "1px solid hsl(var(--border))", background: "var(--bg-card)" }}
          >
            {rosterCrew.map((m, i) => {
              const on = draft.selectedIds.includes(m.id);
              return (
                <label
                  key={m.id}
                  className="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors duration-150"
                  style={{
                    borderTop: i === 0 ? "none" : "1px solid hsl(var(--border))",
                    background: on ? "rgba(var(--accent-green-rgb),0.06)" : "transparent",
                  }}
                >
                  <ReactorToggle checked={on} onChange={() => toggle(m.id)} />
                  <span
                    className="min-w-0 flex-1 truncate text-fs-base font-medium"
                    style={{ color: on ? "var(--text-primary)" : "var(--text-secondary)" }}
                  >
                    {m.name}
                  </span>
                  <span className="flex-shrink-0 font-mono text-fs-label font-medium tracking-[0.14em] text-[color:var(--text-muted)] uppercase">
                    {m.role}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div className="font-mono text-fs-label font-semibold tracking-[0.18em] text-[color:var(--text-muted)] uppercase">
          Custom cards
        </div>
        {draft.custom.map((c, i) => (
          <ListCard
            key={c.id}
            onMoveUp={i > 0 ? () => setCustom(moveItem(draft.custom, i, -1)) : undefined}
            onMoveDown={
              i < draft.custom.length - 1 ? () => setCustom(moveItem(draft.custom, i, 1)) : undefined
            }
            onRemove={() => setCustom(draft.custom.filter((_, j) => j !== i))}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Name">
                <TextInput value={c.name} onChange={(e) => setCard(i, { name: e.target.value })} />
              </Field>
              <Field label="Role">
                <TextInput value={c.role} onChange={(e) => setCard(i, { role: e.target.value })} />
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Badge">
                <TextInput
                  mono
                  value={c.badge ?? ""}
                  onChange={(e) => setCard(i, { badge: e.target.value })}
                />
              </Field>
              <Field label="Accent colour">
                <ColorSelect value={c.color} onChange={(color) => setCard(i, { color })} />
              </Field>
            </div>
            <Field label="Bio">
              <TextArea value={c.bio ?? ""} onChange={(e) => setCard(i, { bio: e.target.value })} />
            </Field>

            {c.agent ? (
              <div
                className="flex flex-col gap-3 rounded-md p-3"
                style={{ background: "var(--bg-panel)", border: "1px solid hsl(var(--border))" }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-fs-label font-semibold tracking-[0.18em] text-[color:var(--text-muted)] uppercase">
                    Paired agent
                  </span>
                  <button
                    type="button"
                    onClick={() => setCard(i, { agent: undefined })}
                    className="text-fs-label font-medium text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--accent-red)]"
                  >
                    Remove agent
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Agent name">
                    <TextInput value={c.agent.name} onChange={(e) => setAgent(i, { name: e.target.value })} />
                  </Field>
                  <Field label="Agent type">
                    <TextInput value={c.agent.type} onChange={(e) => setAgent(i, { type: e.target.value })} />
                  </Field>
                </div>
                <Field label="Agent description">
                  <TextArea
                    value={c.agent.description}
                    onChange={(e) => setAgent(i, { description: e.target.value })}
                  />
                </Field>
                <Field label="Agent accent colour">
                  <ColorSelect value={c.agent.color} onChange={(color) => setAgent(i, { color })} />
                </Field>
              </div>
            ) : (
              <AddButton label="Add paired agent" onClick={() => setAgent(i, {})} />
            )}
          </ListCard>
        ))}
        <AddButton
          label="Add custom card"
          onClick={() => setCustom([...draft.custom, { id: genId("crew"), name: "", role: "" }])}
        />
      </div>
    </SectionFrame>
  );
}
